import React, {useMemo, useState} from 'react';
import {useMutation} from "react-query";
import {apollo} from "../utils/apollo";
import {gql} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {formatCardNumber, formatExpiryDate, getCardType} from "../utils/card";
import pciDssImage from "../assets/images/pci-dss-color.png";
import {
    CreditCardIcon,
    CalendarIcon,
    LockClosedIcon,
    UserIcon,
    ShieldCheckIcon,
    ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import {cn} from "../utils/style";
import CardPreview from "./CardPreview";
import {isIframe} from "../utils/iframe";
import {getDeviceFingerprint} from "../utils/fingerprint";

const SUBMIT_CARD_PAYMENT = gql`
    mutation SubmitCardPayment($input: SubmitCardPaymentInput!) {
        submitCardPayment(input: $input) {
            success
            message
            redirectUrl
        }
    }
`;

function DirectPayForm({uuid, invoice, onBack}) {
    const {t} = useTranslation();
    const [formData, setFormData] = useState({
        card_number: '',
        expiry: '',
        cvv: '',
        card_holder_name: ''
    });

    const cardType = useMemo(() => getCardType(formData.card_number), [formData.card_number]);

    const submitMutation = useMutation({
        mutationFn: async () => {
            const [expiry_month, expiry_year] = formData.expiry.split(' / ');
            const deviceFingerprint = await getDeviceFingerprint();

            const {data} = await apollo.mutate({
                mutation: SUBMIT_CARD_PAYMENT,
                variables: {
                    input: {
                        uuid,
                        card_holder_name: formData.card_holder_name,
                        card_number: formData.card_number.replace(/\s/g, ''),
                        expiry_month,
                        expiry_year,
                        cvv: formData.cvv,
                        device_fingerprint: deviceFingerprint ? {
                            visitor_id: deviceFingerprint.visitor_id,
                            telemetry: deviceFingerprint.telemetry
                        } : null
                    }
                }
            });
            return data?.submitCardPayment;
        },
        onSuccess: (data) => {
            if (data?.redirectUrl) {
                if (isIframe()) {
                    window.open(data.redirectUrl, '_blank');
                } else {
                    window.location.href = data.redirectUrl;
                }
            } else if (!data?.success && !!data?.message) {
                alert(data?.message);
            }
        }
    });

    const handleChange = (e) => {
        let {name, value} = e.target;
        if (name === 'card_number') {
            value = formatCardNumber(value);
        } else if (name === 'expiry') {
            value = formatExpiryDate(value);
        } else if (name === 'cvv') {
            value = value.replace(/[^0-9]/gi, '');
        }
        setFormData({...formData, [name]: value});
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        submitMutation.mutate();
    };


    return (
        <div className="animate-fade-in w-full mx-auto mt-6">
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 overflow-hidden border-0">
                <div className="px-10 py-12 bg-gradient-to-br from-[#0b5f82] to-[#0b9cbc] border-0">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-6 text-center sm:text-left">
                        <div>
                            <h2 className="text-2xl font-black text-white tracking-tight">{t('card.details')}</h2>
                            <p className="text-cyan-100/80 text-sm font-medium mt-1">{t('pay.secure_payment_guaranteed')}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 shadow-inner border border-white/10">
                            <span className="block text-[10px] uppercase font-bold text-cyan-100 tracking-widest mb-1">{t('pay.total_amount')}</span>
                            <span className="text-3xl font-black text-white tracking-tighter">
                                {invoice?.totalAmount} <span className="text-sm font-bold opacity-80">{invoice?.currency?.symbol}</span>
                            </span>
                        </div>
                    </div>
                </div>

                <div className="p-8 sm:p-12 lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start">
                    <div className="lg:order-2 mb-10 lg:mb-0 transform hover:scale-[1.02] transition-transform duration-500 max-w-sm mx-auto lg:mx-0 lg:w-full lg:justify-self-end">
                        <CardPreview
                            cardNumber={formData.card_number}
                            cardHolder={formData.card_holder_name}
                            expiry={formData.expiry}
                            cardType={cardType}
                        />
                    </div>

                    <form onSubmit={handleSubmit} className="lg:order-1 space-y-6">
                        <div className="space-y-5">
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-2">
                                    {t('card.holder_name')}
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-[#0b9cbc] text-gray-400">
                                        <UserIcon className="h-5 w-5" />
                                    </div>
                                    <input
                                        type="text"
                                        name="card_holder_name"
                                        required
                                        placeholder={t('card.holder_name_placeholder')}
                                        value={formData.card_holder_name}
                                        onChange={handleChange}
                                        className="block w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:bg-white focus:ring-4 focus:ring-[#0b9cbc]/10 transition-all duration-300"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-2">
                                    {t('card.number')}
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-[#0b9cbc] text-gray-400">
                                        <CreditCardIcon className="h-5 w-5" />
                                    </div>
                                    <input
                                        type="text"
                                        name="card_number"
                                        required
                                        placeholder={t('card.number_placeholder')}
                                        value={formData.card_number}
                                        onChange={handleChange}
                                        className="block w-full pl-12 pr-12 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:bg-white focus:ring-4 focus:ring-[#0b9cbc]/10 transition-all duration-300"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                        {cardType !== 'unknown' && (
                                            <span className="text-[10px] font-black text-[#0b5f82] uppercase bg-cyan-50 px-2 py-1 rounded-lg">
                                                {cardType}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-2">
                                        {t('card.expiry')}
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-[#0b9cbc] text-gray-400">
                                            <CalendarIcon className="h-5 w-5" />
                                        </div>
                                        <input
                                            type="text"
                                            name="expiry"
                                            placeholder={t('card.expiry_placeholder')}
                                            required
                                            maxLength="7"
                                            value={formData.expiry}
                                            onChange={handleChange}
                                            className="block w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:bg-white focus:ring-4 focus:ring-[#0b9cbc]/10 transition-all duration-300"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-2">
                                        {t('card.cvv')}
                                    </label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-[#0b9cbc] text-gray-400">
                                            <LockClosedIcon className="h-5 w-5" />
                                        </div>
                                        <input
                                            type="password"
                                            name="cvv"
                                            placeholder={t('card.cvv_placeholder')}
                                            required
                                            maxLength="4"
                                            value={formData.cvv}
                                            onChange={handleChange}
                                            className="block w-full pl-12 pr-4 py-4 bg-gray-50 border-0 rounded-2xl text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:bg-white focus:ring-4 focus:ring-[#0b9cbc]/10 transition-all duration-300"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 space-y-4">
                            <button
                                type="submit"
                                disabled={submitMutation.isLoading}
                                className={cn(
                                    "w-full py-4 rounded-2xl text-base font-black text-white shadow-xl shadow-[#0b9cbc]/20 transition-all duration-300 active:scale-[0.98]",
                                    "bg-gradient-to-br from-[#0b5f82] to-[#0b9cbc] hover:from-[#0b5f82] hover:to-[#0b9cbc] border-0",
                                    "disabled:from-gray-400 disabled:to-gray-500 disabled:shadow-none disabled:cursor-not-allowed disabled:transform-none",
                                    "flex items-center justify-center gap-3"
                                )}
                            >
                                {submitMutation.isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>{t('pay.processing')}</span>
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheckIcon className="h-6 w-6 text-white/80" />
                                        <span>{t('pay.pay_now')}</span>
                                    </>
                                )}
                            </button>

                            {onBack && (
                                <button
                                    type="button"
                                    onClick={onBack}
                                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-[#0b9cbc] font-bold transition-all duration-300 py-2"
                                >
                                    <ArrowLeftIcon className="h-4 w-4" />
                                    {t('pay.back_to_payment_choice')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <div className="mt-12 px-4">
                <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
                    <div className="flex items-center gap-3 group">
                        <div className="p-2 rounded-2xl bg-cyan-50 text-[#0b5f82] transition-all duration-300 group-hover:bg-[#0b9cbc]/10 group-hover:scale-110">
                            <ShieldCheckIcon className="h-5 w-5" />
                        </div>
                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{t('pay.secure_payment')}</span>
                    </div>
                    <div className="flex items-center gap-3 group">
                        <div className="p-2 rounded-2xl bg-cyan-50 text-[#0b5f82] transition-all duration-300 group-hover:bg-[#0b9cbc]/10 group-hover:scale-110">
                            <LockClosedIcon className="h-5 w-5" />
                        </div>
                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{t('pay.ssl_encrypted')}</span>
                    </div>
                    <div className="flex items-center grayscale hover:grayscale-0 transition-all duration-500 opacity-40 hover:opacity-100">
                        <img src={pciDssImage} className="h-12 w-auto" alt={t('pay.pci_compliant')} />
                    </div>
                </div>
                <p className="text-center mt-10 text-[10px] text-gray-400 font-medium leading-relaxed max-w-sm mx-auto uppercase tracking-wide opacity-60">
                    {t('pay.security_notice')}
                </p>
            </div>
        </div>
    );
}

export default DirectPayForm;
